
import React, { useState } from 'react';
import { Upload, X, FileImage, Film } from 'lucide-react';
import { useAuth } from '@/contexts/auth';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';

interface MemoryUploaderProps {
  tripId: string;
  onSuccess: () => void;
}

const MemoryUploader: React.FC<MemoryUploaderProps> = ({ tripId, onSuccess }) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [file, setFile] = useState<File | null>(null);
  const [caption, setCaption] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const [fileType, setFileType] = useState<'image' | 'video' | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) {
      clearFile();
      return;
    }
    
    const selectedFile = e.target.files[0];
    
    // Check file size (max 10MB)
    if (selectedFile.size > 10 * 1024 * 1024) {
      toast({
        title: "File too large",
        description: "Please select a file smaller than 10MB",
        variant: "destructive"
      });
      clearFile();
      return;
    }
    
    // Check file type
    if (selectedFile.type.startsWith('image/')) {
      setFileType('image');
    } else if (selectedFile.type.startsWith('video/')) {
      setFileType('video');
    } else {
      toast({
        title: "Invalid file type",
        description: "Please select an image or video file",
        variant: "destructive"
      });
      clearFile();
      return;
    }
    
    setFile(selectedFile);
    
    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreview(reader.result as string);
    };
    reader.readAsDataURL(selectedFile);
  };

  const clearFile = () => {
    setFile(null);
    setPreview(null);
    setFileType(null);
    const fileInput = document.getElementById('memory-file') as HTMLInputElement;
    if (fileInput) fileInput.value = '';
  };

  const handleUpload = async () => {
    if (!file || !user || !fileType) return;
    
    setIsUploading(true);
    
    try {
      // 1. Upload to storage
      const fileExt = file.name.split('.').pop();
      const filePath = `${tripId}/${Date.now()}_${Math.random().toString(36).substring(2)}.${fileExt}`;
      
      const { error: uploadError } = await supabase.storage
        .from('memories')
        .upload(filePath, file);
        
      if (uploadError) throw uploadError;
      
      // 2. Save record in database
      const { error: dbError } = await supabase
        .from('memories')
        .insert({
          trip_id: tripId,
          user_id: user.id,
          file_path: filePath,
          file_type: fileType,
          caption: caption || null
        });
        
      if (dbError) throw dbError;
      
      // Clear form
      setCaption('');
      clearFile();
      onSuccess();
      
    } catch (error) {
      console.error('Error uploading memory:', error);
      toast({
        title: "Upload failed",
        description: "There was an error uploading your memory",
        variant: "destructive"
      });
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="space-y-4">
      {preview ? (
        <div className="relative rounded-md overflow-hidden border-2 border-dashed border-gray-300 h-64">
          <Button 
            variant="destructive" 
            size="icon" 
            className="absolute top-2 right-2 z-10"
            onClick={clearFile}
          >
            <X className="h-4 w-4" />
          </Button>
          
          {fileType === 'image' ? (
            <img 
              src={preview} 
              alt="Preview" 
              className="w-full h-full object-contain"
            />
          ) : (
            <video 
              src={preview} 
              controls
              className="w-full h-full object-contain"
            />
          )}
        </div>
      ) : (
        <label 
          htmlFor="memory-file" 
          className={cn(
            "flex flex-col items-center justify-center w-full h-64 border-2 border-dashed",
            "rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100",
            "border-gray-300 transition-colors"
          )}
        >
          <div className="flex flex-col items-center justify-center pt-5 pb-6">
            <Upload className="w-8 h-8 mb-4 text-gray-500" />
            <p className="mb-2 text-sm text-gray-500">
              <span className="font-semibold">Click to upload</span> or drag and drop
            </p>
            <p className="text-xs text-gray-500">
              Images or videos (max 10MB)
            </p>
            <div className="flex space-x-2 mt-2">
              <FileImage className="h-5 w-5 text-gray-400" />
              <Film className="h-5 w-5 text-gray-400" />
            </div>
          </div>
          <Input
            id="memory-file"
            type="file"
            className="hidden"
            accept="image/*,video/*"
            onChange={handleFileChange}
          />
        </label>
      )}
      
      <Textarea
        placeholder="Add a caption (optional)"
        value={caption}
        onChange={(e) => setCaption(e.target.value)}
        className="resize-none"
      />
      
      <div className="flex justify-end">
        <Button
          onClick={handleUpload}
          disabled={!file || isUploading}
        >
          {isUploading ? "Uploading..." : "Share Memory"}
        </Button>
      </div>
    </div>
  );
};

export default MemoryUploader;
